import type { ReactNode } from 'react';
import { ClipboardPaste, Pin, Trash2 } from 'lucide-react';
import { Textarea } from '@/app/components/ui/textarea';
import { Checkbox } from '@/app/components/ui/checkbox';
import { TabsContent } from '@/app/components/ui/tabs';
import { CharBar } from '../CharBar';
import { addVariant, removeVariant, updateVariant } from '../useTemplateEditor';
import type { EditorReady } from './types';

type Props = {
  ed: EditorReady;
  adBar: ReactNode;
  fieldPalette: ReactNode;
  liveAdPreviewMini: ReactNode;
  filledDescriptions: number;
};

export function DescriptionsTab({ ed, adBar, fieldPalette, liveAdPreviewMini, filledDescriptions }: Props) {
  const {
    activeDescriptions, isMultiAd, activeAdIndex,
    expandedVariants, setExpandedVariants, allVariantsExpanded, setAllVariantsExpanded,
    updateActiveDescriptionSlot, updateActiveDescriptionSlotProp,
    handleActiveSlotPaste, pasteActiveDescriptions,
    inputProps, getPreviewText, getCharInfo,
  } = ed;

  return (
    <TabsContent value="descriptions">
      {liveAdPreviewMini}
      {adBar}
      {fieldPalette}
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="grid gap-3 px-3 py-1.5 bg-muted/40 border-b border-border text-[10px] font-mono text-muted-foreground uppercase tracking-widest items-center"
          style={{ gridTemplateColumns: '36px 28px 1.5fr 1fr 72px 28px' }}>
          <span>#</span>
          <span title="Pin to position"><Pin className="size-2.5 opacity-40" /></span>
          <span className="flex items-center gap-2">
            Template
            <button
              className="text-[11px] font-mono text-muted-foreground/70 hover:text-primary border border-border/50 hover:border-primary/40 hover:bg-primary/5 rounded px-2 py-0.5 transition-colors normal-case tracking-normal flex items-center gap-1.5"
              title="Paste descriptions from clipboard (one per line)"
              onClick={() => pasteActiveDescriptions()}
            ><ClipboardPaste className="size-3" />Paste</button>
          </span>
          <span className="flex items-center gap-2">
            Preview
            <button
              className="ml-1 text-[9px] font-mono text-muted-foreground/40 hover:text-primary border border-border/40 rounded px-1 py-0.5 transition-colors"
              onClick={() => {
                const allKeys: Record<string, boolean> = {};
                activeDescriptions.forEach(s => { allKeys[`d:${s.slot_number}`] = !allVariantsExpanded; });
                setExpandedVariants(prev => ({ ...prev, ...allKeys }));
                setAllVariantsExpanded(!allVariantsExpanded);
              }}
            >{allVariantsExpanded ? '− collapse all' : '+ expand all'}</button>
          </span>
          <span>Chars</span><span />
        </div>
        {activeDescriptions.map((slot) => {
          const primary = slot.variants[0] ?? { variant_order: 1, template_string: '', enabled: true };
          const extras = slot.variants.slice(1);
          const isExpanded = expandedVariants[`d:${slot.slot_number}`] ?? false;
          const hasContent = !!primary.template_string.trim();
          return (
            <div key={slot.slot_number} className="border-b border-border/40 last:border-0">
              <div
                className={`grid gap-3 px-3 py-2 items-stretch group hover:bg-muted/20 transition-colors ${!hasContent ? 'opacity-50' : ''}`}
                style={{ gridTemplateColumns: '36px 28px 1.5fr 1fr 72px 28px' }}
              >
                <span className="text-[11px] font-mono text-muted-foreground flex items-start pt-1">D{slot.slot_number}</span>
                <button
                  className={`size-5 flex items-center justify-center rounded text-[9px] font-mono transition-colors mt-1 ${
                    slot.pinned_position
                      ? 'bg-primary/15 text-primary border border-primary/30'
                      : 'text-muted-foreground/20 hover:text-muted-foreground/50'
                  }`}
                  title={slot.pinned_position ? `Pinned to position ${slot.pinned_position} — click to change` : 'Pin to position'}
                  onClick={() => {
                    const next = slot.pinned_position === undefined ? 1 : slot.pinned_position >= 2 ? undefined : (slot.pinned_position + 1) as 1 | 2;
                    updateActiveDescriptionSlotProp(slot.slot_number, { pinned_position: next });
                  }}
                >
                  {slot.pinned_position ? slot.pinned_position : <Pin className="size-2.5" />}
                </button>
                <Textarea
                  value={primary.template_string}
                  onChange={e => updateActiveDescriptionSlot(slot.slot_number, updateVariant(slot.variants, 1, { template_string: e.target.value }))}
                  onPaste={e => handleActiveSlotPaste(e, 'description', slot.slot_number)}
                  placeholder={`Description ${slot.slot_number}…`}
                  className="min-h-0 h-[54px] text-sm resize-none border-0 border-b border-border/40 rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-primary px-0 py-0 self-start"
                  {...inputProps(`d:${slot.slot_number}:1`)}
                />
                <div className="text-xs text-primary/70 self-start leading-relaxed pt-1" title={getPreviewText(primary.template_string)}
                  style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                  {getPreviewText(primary.template_string) || <span className="text-muted-foreground/30 italic">—</span>}
                </div>
                <div className="flex items-start pt-1"><CharBar {...getCharInfo(primary.template_string, 90)} limit={90} /></div>
                <button
                  className="size-7 flex items-start justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors pt-1"
                  onClick={() => {
                    const key = `d:${slot.slot_number}`;
                    if (!isExpanded && extras.length === 0) updateActiveDescriptionSlot(slot.slot_number, addVariant(slot.variants));
                    setExpandedVariants(prev => ({ ...prev, [key]: !prev[key] }));
                  }}
                >
                  {extras.length > 0
                    ? <span className="text-[10px] font-mono text-primary/60">+{extras.length}</span>
                    : <span className="text-[13px] opacity-30 group-hover:opacity-70">⊕</span>}
                </button>
              </div>
              {isExpanded && (
                <>
                  {extras.map((v) => (
                    <div key={v.variant_order}
                      className="grid gap-3 px-3 py-2 items-start bg-muted/10 border-t border-dashed border-border/40"
                      style={{ gridTemplateColumns: '36px 28px 1.5fr 1fr 72px 28px' }}>
                      <span className="text-[10px] font-mono text-muted-foreground/50 pt-1">V{v.variant_order}</span>
                      <span />
                      <Textarea
                        value={v.template_string}
                        onChange={e => updateActiveDescriptionSlot(slot.slot_number, updateVariant(slot.variants, v.variant_order, { template_string: e.target.value }))}
                        placeholder="Fallback…"
                        className="min-h-0 h-[54px] text-sm resize-none border-0 border-b border-dashed border-border/40 rounded-none bg-transparent focus-visible:ring-0 px-0 py-0"
                        {...inputProps(`d:${slot.slot_number}:${v.variant_order}`)}
                      />
                      <div className="text-xs text-primary/60 pt-1 leading-relaxed" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{getPreviewText(v.template_string)}</div>
                      <div className="pt-1"><CharBar {...getCharInfo(v.template_string, 90)} limit={90} /></div>
                      <button
                        className="size-7 flex items-center justify-center rounded text-muted-foreground/40 hover:text-destructive hover:bg-destructive/10 transition-colors mt-0.5"
                        onClick={() => updateActiveDescriptionSlot(slot.slot_number, removeVariant(slot.variants, v.variant_order))}
                      ><Trash2 className="size-3" /></button>
                    </div>
                  ))}
                  <div className="px-3 py-1.5 bg-muted/10 border-t border-dashed border-border/30 flex items-center justify-between">
                    <button className="text-xs font-mono text-muted-foreground/50 hover:text-primary transition-colors"
                      onClick={() => updateActiveDescriptionSlot(slot.slot_number, addVariant(slot.variants))}>
                      + add fallback
                    </button>
                    <label className="flex items-center gap-1.5 cursor-pointer" title="Leave this slot empty if no variant fits within the character limit">
                      <Checkbox
                        checked={slot.skip_if_too_long ?? false}
                        onCheckedChange={checked => updateActiveDescriptionSlotProp(slot.slot_number, { skip_if_too_long: !!checked })}
                        className="size-3.5"
                      />
                      <span className="text-[10px] text-muted-foreground/60">Skip if all variants too long</span>
                    </label>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-muted-foreground/50 mt-2 font-mono">{filledDescriptions}/4 set · min 2 required{isMultiAd ? ` · Ad ${activeAdIndex}` : ''}</p>
    </TabsContent>
  );
}
